import React, { Suspense, lazy, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import { LandingPage } from "@/components/LandingPage";
import SHARE_PAGES from "@/lib/sharePages.json";
import { AuthProvider, AuthCallback, GitHubAuthCallback } from "@/components/AuthContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { I18nProvider } from "@/i18n/I18nContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import { SupportWidget } from "@/components/SupportWidget";
import { GlobalUpgradePrompt } from "@/components/GlobalUpgradePrompt";
import { SeoMeta } from "@/components/SeoMeta";
import { HumanCheckModal } from "@/components/HumanCheckModal";
import { UnsavedChangesGuard } from "@/components/UnsavedChangesGuard";
import { captureReferralFromUrl } from "@/lib/referral";

// Every page except the landing page is code-split so "/" doesn't ship the whole app.
const PublicResume = lazy(() => import("@/components/PublicResume").then((m) => ({ default: m.PublicResume })));
const PortfolioSite = lazy(() => import("@/components/PortfolioSite").then((m) => ({ default: m.PortfolioSite })));
const PublicProfile = lazy(() => import("@/components/PublicProfile").then((m) => ({ default: m.PublicProfile })));
const TalentDirectory = lazy(() => import("@/components/TalentDirectory").then((m) => ({ default: m.TalentDirectory })));
const PaymentSuccess = lazy(() => import("@/components/PaymentSuccess").then((m) => ({ default: m.PaymentSuccess })));
const ResetPassword = lazy(() => import("@/components/ResetPassword").then((m) => ({ default: m.ResetPassword })));
const VerifyEmail = lazy(() => import("@/components/VerifyEmail").then((m) => ({ default: m.VerifyEmail })));
const AdminDashboard = lazy(() => import("@/components/AdminDashboard").then((m) => ({ default: m.AdminDashboard })));
const ContactPage = lazy(() => import("@/components/ContactPage").then((m) => ({ default: m.ContactPage })));
const AboutPage = lazy(() => import("@/components/AboutPage").then((m) => ({ default: m.AboutPage })));
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage").then((m) => ({ default: m.TemplatesPage })));
const ComparePage = lazy(() => import("@/pages/ComparePage").then((m) => ({ default: m.ComparePage })));
const AtsResumeCheckerPage = lazy(() => import("@/pages/AtsResumeCheckerPage").then((m) => ({ default: m.AtsResumeCheckerPage })));
const NewApplicationPage = lazy(() => import("@/pages/NewApplicationPage").then((m) => ({ default: m.NewApplicationPage })));
const CareerQuestionnairePage = lazy(() => import("@/pages/CareerQuestionnairePage").then((m) => ({ default: m.CareerQuestionnairePage })));
const AiCareerBuilderPage = lazy(() => import("@/pages/AiCareerBuilderPage").then((m) => ({ default: m.AiCareerBuilderPage })));
const CareerProfilePage = lazy(() => import("@/pages/CareerProfilePage").then((m) => ({ default: m.CareerProfilePage })));
const ApplicationBundleWorkspacePage = lazy(() => import("@/pages/ApplicationBundleWorkspacePage").then((m) => ({ default: m.ApplicationBundleWorkspacePage })));
const ResumeOptimizerPage = lazy(() => import("@/pages/ResumeOptimizerPage").then((m) => ({ default: m.ResumeOptimizerPage })));
const SmartStyleStudioPage = lazy(() => import("@/pages/SmartStyleStudioPage").then((m) => ({ default: m.SmartStyleStudioPage })));
const JobMatchPage = lazy(() => import("@/pages/JobMatchPage").then((m) => ({ default: m.JobMatchPage })));
const LinkedInToResumePage = lazy(() => import("@/pages/LinkedInToResumePage").then((m) => ({ default: m.LinkedInToResumePage })));
const DemoPage = lazy(() => import("@/pages/DemoPage").then((m) => ({ default: m.DemoPage })));
const PrivacyPolicyPage = lazy(() => import("@/components/PrivacyPolicyPage").then((m) => ({ default: m.PrivacyPolicyPage })));
const RefundPolicyPage = lazy(() => import("@/components/RefundPolicyPage").then((m) => ({ default: m.RefundPolicyPage })));
const TermsOfServicePage = lazy(() => import("@/components/TermsOfServicePage").then((m) => ({ default: m.TermsOfServicePage })));
const CookiePolicyPage = lazy(() => import("@/components/CookiePolicyPage").then((m) => ({ default: m.CookiePolicyPage })));
const LoginPage = lazy(() => import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const AppShell = lazy(() => import("@/components/AppShell").then((m) => ({ default: m.AppShell })));
const DashboardHomePage = lazy(() => import("@/pages/DashboardHomePage").then((m) => ({ default: m.DashboardHomePage })));
const CreatePage = lazy(() => import("@/pages/CreatePage").then((m) => ({ default: m.CreatePage })));
const MyResumesPage = lazy(() => import("@/pages/MyResumesPage").then((m) => ({ default: m.MyResumesPage })));
const JobSearchPage = lazy(() => import("@/pages/JobSearchPage").then((m) => ({ default: m.JobSearchPage })));
const JobDetailPage = lazy(() => import("@/pages/JobDetailPage").then((m) => ({ default: m.JobDetailPage })));
const ApplicationWorkspacePage = lazy(() => import("@/pages/ApplicationWorkspacePage").then((m) => ({ default: m.ApplicationWorkspacePage })));
const TrackerPage = lazy(() => import("@/pages/TrackerPage").then((m) => ({ default: m.TrackerPage })));
const AIStudioPage = lazy(() => import("@/pages/AIStudioPage").then((m) => ({ default: m.AIStudioPage })));
const StyleStudioLandingPage = lazy(() => import("@/pages/StyleStudioLandingPage").then((m) => ({ default: m.StyleStudioLandingPage })));
const StyleStudioWorkspacePage = lazy(() => import("@/pages/StyleStudioWorkspacePage").then((m) => ({ default: m.StyleStudioWorkspacePage })));
const AtsAnalyzerPage = lazy(() => import("@/pages/AtsAnalyzerPage").then((m) => ({ default: m.AtsAnalyzerPage })));
const ResumeCompilerPage = lazy(() => import("@/pages/ResumeCompilerPage").then((m) => ({ default: m.ResumeCompilerPage })));
const DigitalPortfolioPage = lazy(() => import("@/pages/DigitalPortfolioPage").then((m) => ({ default: m.DigitalPortfolioPage })));
const AtsOptimizerLandingPage = lazy(() => import("@/pages/AtsOptimizerLandingPage").then((m) => ({ default: m.AtsOptimizerLandingPage })));
const AtsOptimizerAnalyzePage = lazy(() => import("@/pages/AtsOptimizerAnalyzePage").then((m) => ({ default: m.AtsOptimizerAnalyzePage })));
const AtsOptimizerReportPage = lazy(() => import("@/pages/AtsOptimizerReportPage").then((m) => ({ default: m.AtsOptimizerReportPage })));
const AtsOptimizerVersionsPage = lazy(() => import("@/pages/AtsOptimizerVersionsPage").then((m) => ({ default: m.AtsOptimizerVersionsPage })));
const JobMatchAnalyzerPage = lazy(() => import("@/pages/JobMatchAnalyzerPage").then((m) => ({ default: m.JobMatchAnalyzerPage })));
const CoverLetterGeneratorPage = lazy(() => import("@/pages/CoverLetterGeneratorPage").then((m) => ({ default: m.CoverLetterGeneratorPage })));
const LinkedInOptimizerPage = lazy(() => import("@/pages/LinkedInOptimizerPage").then((m) => ({ default: m.LinkedInOptimizerPage })));
const ProfilePage = lazy(() => import("@/pages/ProfilePage").then((m) => ({ default: m.ProfilePage })));
const SettingsPage = lazy(() => import("@/pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const ReferralsPage = lazy(() => import("@/pages/ReferralsPage").then((m) => ({ default: m.ReferralsPage })));
const ResumeEditorPage = lazy(() => import("@/pages/ResumeEditorPage").then((m) => ({ default: m.ResumeEditorPage })));

// Refer-a-friend: remember ?ref=<CODE> from any landing URL (lib/referral.js).
captureReferralFromUrl();

function useSharedTheme() {
  // Dark is the default again (explicit user request, 2026-09-28: "make
  // dark mode as default view"). The old key was written on EVERY first
  // visit, even without a choice, so every past visitor had "light" saved
  // and would never see a new default -- the preference now lives under a
  // new key that is only written when someone actually toggles.
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("rp360_theme_choice") || "dark"; } catch { return "dark"; }
  });
  const toggleTheme = () => setTheme((t) => {
    const next = t === "dark" ? "light" : "dark";
    try { localStorage.setItem("rp360_theme_choice", next); } catch {}
    return next;
  });
  return [theme, toggleTheme];
}

// "/" is the marketing page for everyone, signed in or not -- signed-in
// visitors just get a header that reflects that (account chip instead of
// Sign In, per LandingPage's own logic) instead of being bounced away, so
// there's always a real way back to it (the AppShell sidebar logo links
// here). Login/register only redirect to the Dashboard right after a
// successful auth, not on every future visit to "/".
function LandingOrRedirect({ theme, onToggleTheme }) {
  const navigate = useNavigate();

  return (
    <LandingPage
      onStartBuilding={() => navigate("/editor", { state: { startNew: true, initialAiMode: "chat" } })}
      onSelectTemplate={(idx, styleParams) => navigate("/editor", { state: { startNew: true, selectedTemplate: idx, openStyleDrawer: true, styleParams } })}
      // AI Career Workspace Phase 1: initialAiMode="chat" is the one thing
      // missing to land the user in the AI Copilot pane instead of Content
      // -- everything else (resumeData incl. generationMode, the ATS/job-
      // match summary, AiChatPanel's own initial-prompt auto-send with full
      // resume context, the persistent preview, the resizable divider) was
      // already wired end-to-end before this change; only the workspace's
      // starting tab was left at its Content default.
      onStartWithData={(data, templateIdx = 0, initialChatPrompt = "", summary = {}) => navigate("/editor", { state: { importedData: data, selectedTemplate: templateIdx, initialChatPrompt, initialAiMode: "chat", importedCode: summary.code || null, importedAtsResult: summary.atsResult || null } })}
      onGoDashboard={() => navigate("/dashboard")}
      theme={theme}
      onToggleTheme={onToggleTheme}
    />
  );
}

function RouteFallback() {
  return (
    <div role="status" aria-label="Loading" className="flex min-h-screen items-center justify-center">
      <div className="size-6 animate-spin rounded-full border-2 border-current border-r-transparent opacity-40" />
    </div>
  );
}

function AppRouter({ theme, toggleTheme }) {
  const location = useLocation();

  // Process Google Auth callback synchronously (before any other route/auth logic)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/r/:code" element={<PublicResume />} />
      <Route path="/p/:code" element={<PortfolioSite />} />
      <Route path="/u/:username" element={<PublicProfile />} />
      <Route path="/talent" element={<TalentDirectory />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/login" element={<LoginPage />} />
      {/* /signup (approved 2026-08-16): same LoginPage component, opened on
          its "register" mode -- see that file's own defaultMode comment,
          there's no separate signup page. */}
      <Route path="/signup" element={<LoginPage defaultMode="register" />} />
      <Route path="/auth/github/callback" element={<GitHubAuthCallback />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/contact" element={<ContactPage isLight={theme === "light"} />} />
      <Route path="/about" element={<AboutPage isLight={theme === "light"} />} />
      <Route path="/privacy" element={<PrivacyPolicyPage isLight={theme === "light"} />} />
      <Route path="/refund" element={<RefundPolicyPage isLight={theme === "light"} />} />
      <Route path="/terms" element={<TermsOfServicePage isLight={theme === "light"} />} />
      <Route path="/cookies" element={<CookiePolicyPage isLight={theme === "light"} />} />
      <Route path="/templates" element={<TemplatesPage isLight={theme === "light"} />} />
      <Route path="/compare" element={<ComparePage isLight={theme === "light"} />} />
      {/* Public SEO landing pages for individual tools (frontend-modernization
          port from the career-canvas Lovable prototype) -- marketing/explainer
          layers in front of the real in-app tools at /ats-optimizer and
          /style-studio, not duplicate implementations. */}
      <Route path="/ats-resume-checker" element={<AtsResumeCheckerPage isLight={theme === "light"} />} />
      {/* Application Workspace, Flow #1 of 3 (Upload Existing Resume) --
          deliberately standalone top-level routes, NOT nested under
          <AppShell>: this must feel like a separate application workflow,
          not another dashboard/editor screen (explicit spec requirement).
          /applications/new/ApplicationBundleWorkspacePage.jsx own module
          comments have the full design; both are additive and don't touch
          any existing route, component, or AI engine. */}
      <Route path="/applications/new" element={<NewApplicationPage />} />
      {/* Flow #2 (Career Questionnaire) / Flow #3 (AI Career Builder) --
          Intake Session convergence: both drive resume-service's
          routes_intake.py to a resume_code, then hand off to the SAME
          POST /applications + /applications/:id Flow #1 already uses. See
          CareerQuestionnairePage.jsx / AiCareerBuilderPage.jsx. */}
      <Route path="/applications/new/questionnaire" element={<CareerQuestionnairePage />} />
      <Route path="/applications/new/ai-builder" element={<AiCareerBuilderPage />} />
      {/* Career Profile (2026-09-22) -- persistent, per-user structured career
          record replacing Flow #2's old one-shot questionnaire, built section
          by section and revisited freely (not tied to a single application).
          Standalone route with its own header/layout, same as Flow #2/#3
          above, not nested under AppShell (which wraps children in dashboard
          sidebar chrome this page doesn't use). See CareerProfilePage.jsx and
          routes_career_data_profile.py's module docstrings. Not yet linked
          from the landing page or navigation -- Phase 5 wires that up. */}
      <Route path="/applications/:id" element={<ApplicationBundleWorkspacePage />} />
      <Route path="/resume-optimizer" element={<ResumeOptimizerPage isLight={theme === "light"} />} />
      <Route path="/smart-style-studio" element={<SmartStyleStudioPage isLight={theme === "light"} />} />
      <Route path="/job-match" element={<JobMatchPage isLight={theme === "light"} />} />
      <Route path="/linkedin-to-resume" element={<LinkedInToResumePage isLight={theme === "light"} />} />
      <Route path="/demo" element={<DemoPage isLight={theme === "light"} />} />
      {/* Social share links (/job-match.html etc., scripts/build-share-pages.js).
          In production the static copy already swaps the URL before boot;
          this catches the dev server, which serves plain index.html there. */}
      {SHARE_PAGES.map((p) => (
        <Route key={p.slug} path={`/${p.slug}.html`} element={<Navigate to={p.route} replace />} />
      ))}
      <Route path="/" element={<LandingOrRedirect theme={theme} onToggleTheme={toggleTheme} />} />

      <Route element={<AppShell theme={theme} onToggleTheme={toggleTheme} />}>
        <Route path="/dashboard" element={<DashboardHomePage />} />
        <Route path="/create" element={<CreatePage />} />
        <Route path="/resumes" element={<MyResumesPage />} />
        {/* Inside the app shell since 2026-10-06 (sidebar + app theme). */}
        <Route path="/career-profile" element={<CareerProfilePage />} />
        <Route path="/jobs" element={<JobSearchPage />} />
        <Route path="/jobs/:matchId" element={<JobDetailPage />} />
        <Route path="/jobs/:matchId/workspace" element={<ApplicationWorkspacePage />} />
        <Route path="/tracker" element={<TrackerPage />} />
        <Route path="/ai-studio" element={<AIStudioPage />} />
        {/* AI Style Studio Dashboard destination (added 2026-09-05, see
            AppShell.jsx's NAV_SECTIONS comment) -- a resume picker only;
            hands off into the real docked panel at /editor/:code, never a
            second Style Studio implementation. */}
        <Route path="/style-studio" element={<StyleStudioLandingPage />} />
        {/* AI Style Studio's dedicated design workspace (added 2026-09,
            explicit user request: the sidebar destination must open a real
            canvas-first design workspace, not the general Resume Editor
            with a panel toggled open) -- see StyleStudioWorkspacePage.jsx's
            own comment. /editor/:code and its in-editor Style Studio toggle
            are untouched. */}
        <Route path="/style-studio/:code" element={<StyleStudioWorkspacePage />} />
        <Route path="/ats-analyzer" element={<AtsAnalyzerPage />} />
        <Route path="/ats-analyzer/:code" element={<AtsAnalyzerPage />} />
        {/* ATS Optimizer360 Phase 1 (approved 2026-08-17) -- multi-page flow:
            Resume -> Target Job -> Workspace -> Report/Versions. The
            workspace route reuses AtsAnalyzerPage directly (same component,
            same target-role/race-condition fixes, same resume-scoped chat
            storage) rather than a second, duplicate workspace UI. */}
        <Route path="/ats-optimizer" element={<AtsOptimizerLandingPage />} />
        <Route path="/ats-optimizer/analyze/:code" element={<AtsOptimizerAnalyzePage />} />
        <Route path="/ats-optimizer/workspace/:code" element={<AtsAnalyzerPage />} />
        <Route path="/ats-optimizer/report/:code" element={<AtsOptimizerReportPage />} />
        <Route path="/ats-optimizer/versions" element={<AtsOptimizerVersionsPage />} />
        {/* Resume Compiler Intelligence workspace (2026-08-20) -- full
            first-class page replacing CompileResumeModal's popup, additive
            for now (not yet wired from any entry point -- see
            ResumeCompilerPage.jsx's own module comment). */}
        <Route path="/resume-compiler" element={<ResumeCompilerPage />} />
        <Route path="/resume-compiler/:code" element={<ResumeCompilerPage />} />
        <Route path="/job-match-analyzer" element={<JobMatchAnalyzerPage />} />
        <Route path="/job-match-analyzer/:code" element={<JobMatchAnalyzerPage />} />
        {/* Digital Portfolio · Go Live, its own workspace (explicit user
            request, 2026-09-26) -- see DigitalPortfolioPage.jsx's own
            module comment for why this is additive alongside (not a
            replacement for) ResumeEditor.jsx's own Publish tab. */}
        <Route path="/digital-portfolio" element={<DigitalPortfolioPage />} />
        <Route path="/digital-portfolio/:code" element={<DigitalPortfolioPage />} />
        <Route path="/cover-letter-generator" element={<CoverLetterGeneratorPage />} />
        <Route path="/cover-letter-generator/:code" element={<CoverLetterGeneratorPage />} />
        <Route path="/ai-studio/linkedin" element={<LinkedInOptimizerPage />} />
        <Route path="/ai-studio/linkedin/:code" element={<LinkedInOptimizerPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/referrals" element={<ReferralsPage />} />
        <Route path="/editor" element={<ResumeEditorPage />} />
        <Route path="/editor/:code" element={<ResumeEditorPage />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  const [theme, toggleTheme] = useSharedTheme();
  return (
    <BrowserRouter>
      <GoogleOAuthProvider clientId={googleClientId}>
        <I18nProvider>
          <ThemeProvider mode={theme} onToggleMode={toggleTheme}>
            <AuthProvider>
              {/* top-right, not bottom-right: bottom-right is where the AI Copilot
                  panel's action buttons (e.g. ATS Approve/Skip) and the floating
                  support-chat button both live, so a bottom-right toast can pop up
                  directly on top of a button the user is about to click. */}
              <Toaster position="top-right" closeButton theme={theme} />
              <AppRouter theme={theme} toggleTheme={toggleTheme} />
              {/* Opens the plan picker whenever a free try runs out, app-wide. */}
              <GlobalUpgradePrompt isLight={theme === "light"} />
              <SeoMeta />
              <HumanCheckModal isLight={theme === "light"} />
              <UnsavedChangesGuard isLight={theme === "light"} />
              <SupportWidget />
            </AuthProvider>
          </ThemeProvider>
        </I18nProvider>
      </GoogleOAuthProvider>
    </BrowserRouter>
  );
}

export default App;
